import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import Footer from '@/components/Footer';
import Header from '@/components/Header';
import FrequentlyAskedQuestions from '@/components/shared/FrequentlyAskedQuestions';
import ProvenResults from '@/components/shared/ProvenResults';
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import SEO from '@/components/shared/SEO';

const Pricing = () => {
  const [selectedInterval, setSelectedInterval] = useState<'month' | 'year'>('month');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const navigate = useNavigate();
  
  const handleSignUp = () => {
    navigate('/');
  };
  
  const plansToRender = [
    {
      displayName: 'Basic',
      title: 'BASIC (FREE FOREVER)',
      price: 0,
      description: 'For startups & teams exploring AI-powered hiring.',
      features: [
        { name: '20 Candidate Assessments', included: true },
        { name: 'Create Unlimited Assessments', included: true },
        { name: 'ATS Integration', included: false },
        { name: 'AI Detector (Flags AI-generated responses)', included: false },
        { name: 'Single Sign-On (SSO)', included: false },
        { name: 'Custom Questions (Collect responses to specific questions)', included: false },
        { name: 'Custom Domain and Branding', included: false },
        { name: 'Priority Phone Support', included: false },
        { name: 'Dedicated Account Manager', included: false }
      ]
    },
    {
      displayName: 'Premium',
      title: 'PREMIUM',
      price: selectedInterval === 'month' ? 249 : 2490,
      description: 'For small TA teams focused on hiring top talent quickly',
      features: [
        { name: '100 Candidate Assessments', included: true },
        { name: 'Create Unlimited Assessments', included: true },
        { name: 'ATS Integration', included: true },
        { name: 'AI Detector (Flags AI-generated responses)', included: true },
        { name: 'Single Sign-On (SSO)', included: false },
        { name: 'Custom Questions (Collect responses to specific questions)', included: true },
        { name: 'Custom Domain and Branding', included: false },
        { name: 'Priority Phone Support', included: false },
        { name: 'Dedicated Account Manager', included: false }
      ]
    },
    {
      displayName: 'Agency',
      title: 'AGENCY',
      price: selectedInterval === 'month' ? 999 : 9990,
      description: 'For agencies and enterprises with high-volume hiring needs.',
      isPopular: true,
      features: [
        { name: '500 Candidate Assessments', included: true },
        { name: 'Create Unlimited Assessments', included: true },
        { name: 'ATS Integration', included: true },
        { name: 'AI Detector (Flags AI-generated responses)', included: true },
        { name: 'Single Sign-On (SSO)', included: true },
        { name: 'Custom Questions (Collect responses to specific questions)', included: true },
        { name: 'Custom Domain and Branding', included: true },
        { name: 'Priority Phone Support', included: true },
        { name: 'Dedicated Account Manager', included: true }
      ]
    }
  ];
  
  const frequentlyAskedQuestions = [
    {
      question: "Can I change my plan later?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes will take effect at the start of your next billing cycle."
    },
    {
      question: "What happens when I reach my assessment limit?",
      answer: "If you reach your monthly assessment limit, please reach out to hello@roundone.ai to request an advance top-up of credits."
    },
    {
      question: "Is there a free trial available?",
      answer: "When you join RoundOne, you are assigned a Basic plan which includes 20 candidate assessment credits every month. For most small teams this will be sufficient to get started and test the platform before deciding on upgrading for paid plans."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel your subscription at any time. You'll continue to have access to your plan until the end of your current billing period."
    },
    {
      question: "Do you offer discounts for non-profits or educational institutions?",
      answer: "Yes, we offer special pricing for qualified non-profit organizations, educational institutions, and startups. Please contact our sales team for more information."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards including Visa, Mastercard, and American Express. For Enterprise plans, we can also arrange invoicing and wire transfers."
    }
  ];
  
  // Create pricing structured data
  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "RoundOne AI Assessment Platform",
    "description": "AI-powered candidate assessment platform for smarter hiring decisions",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "GBP",
      "lowPrice": 0,
      "highPrice": 999,
      "offerCount": 3,
      "offers": [
        {
          "@type": "Offer",
          "name": "Basic",
          "price": 0,
          "priceCurrency": "GBP",
          "description": "For startups & teams exploring AI-powered hiring",
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "name": "Premium",
          "price": selectedInterval === 'month' ? 249 : 2490,
          "priceCurrency": "GBP",
          "description": "For small TA teams focused on hiring top talent quickly",
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "name": "Agency",
          "price": selectedInterval === 'month' ? 999 : 9990,
          "priceCurrency": "GBP",
          "description": "For agencies and enterprises with high-volume hiring needs",
          "availability": "https://schema.org/InStock"
        }
      ]
    }
  };

  // Create breadcrumb structured data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://roundone.ai"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pricing",
        "item": "https://roundone.ai/pricing"
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO 
        title="Pricing - RoundOne AI"
        description="Simple, transparent pricing for AI-powered candidate assessment. Choose the plan that works best for your hiring needs."
        url="/pricing"
        schema={[pricingSchema, breadcrumbSchema]}
      />

      <Header />
      
      {/* Main content */}
      <main className="flex-1">
        {/* Hero section */}
        <section className="bg-roundone-dark-purple py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl font-bold mb-6 text-white">Simple, Transparent Pricing</h1>
              <p className="text-lg text-roundone-white/80 mb-8">
                Choose the plan that works best for your screening needs.
                All plans include our core AI screening technology.
              </p>
              
              {/* Billing toggle */}
              <div className="inline-flex items-center justify-center bg-roundone-purple/20 rounded-full p-1">
                <ToggleGroup type="single" value={billingCycle} onValueChange={(value) => {
                  if (value === 'monthly' || value === 'annual') {
                    setBillingCycle(value);
                  }
                }}>
                  <ToggleGroupItem value="monthly" className={`px-4 py-2 text-sm rounded-full transition-all ${billingCycle === 'monthly' ? 'bg-roundone-purple text-white' : 'text-white/70 hover:text-white'}`}>
                    Monthly
                  </ToggleGroupItem>
                  <ToggleGroupItem value="annual" className={`px-4 py-2 text-sm rounded-full transition-all ${billingCycle === 'annual' ? 'bg-roundone-purple text-white' : 'text-white/70 hover:text-white'}`}>
                    Annual <span className="text-xs ml-1 opacity-80">Save 20%</span>
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>
          </div>
        </section>
        
        {/* Pricing plans */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="grid gap-4 md:grid-cols-3 max-w-5xl mx-auto">
              {plansToRender.map((plan) => (
                <div
                  key={plan.displayName}
                  className={`${
                    plan.isPopular 
                      ? 'bg-roundone-dark-purple rounded-2xl shadow-lg' 
                      : 'bg-[#F8F7FF] rounded-2xl'
                  } p-5 relative overflow-hidden flex flex-col`}
                >
                  {plan.isPopular && (
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-[#B197FC] text-white border-none uppercase text-xs font-medium px-3 py-0.5 rounded-full">
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  
                  <div className="min-h-[140px]">
                    <h3 className={`text-xs font-medium uppercase tracking-wide ${
                      plan.isPopular ? 'text-white/80' : 'text-muted-foreground'
                    }`}>
                      {plan.title}
                    </h3>

                    <div className="flex items-baseline gap-1 mt-3">
                      <span className={`text-4xl font-bold ${
                        plan.isPopular ? 'text-white' : 'text-foreground'
                      }`}>
                        £{plan.price}
                      </span>
                      <span className={`text-xs ${
                        plan.isPopular ? 'text-white/60' : 'text-muted-foreground'
                      }`}>
                        /{selectedInterval}
                      </span>
                    </div>

                    <p className={`text-xs mt-3 ${
                      plan.isPopular ? 'text-white/60' : 'text-muted-foreground'
                    }`}>
                      {plan.description}
                    </p>
                  </div>

                  <div className="flex-grow space-y-2">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-2">
                        {feature.included ? (
                          <Check className={`h-4 w-4 flex-shrink-0 ${
                            plan.isPopular ? 'text-white' : 'text-roundone-purple'
                          }`} />
                        ) : (
                          <X className={`h-4 w-4 flex-shrink-0 ${
                            plan.isPopular ? 'text-white/40' : 'text-muted-foreground/40'
                          }`} />
                        )}
                        <span className={`text-xs ${
                          plan.isPopular 
                            ? feature.included ? 'text-white' : 'text-white/40'
                            : feature.included ? 'text-foreground' : 'text-muted-foreground/60'
                        }`}>
                          {feature.name}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5">
                    <Button
                      className={`w-full h-9 rounded-full text-sm font-medium ${
                        plan.displayName === 'Basic'
                          ? 'bg-[#B197FC] text-white hover:bg-[#B197FC]/90'
                          : 'bg-roundone-purple text-white hover:bg-roundone-purple/90'
                      }`}
                      onClick={handleSignUp}
                    >
                      Get Started
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Enterprise banner */}
            <div className="mt-16 max-w-6xl mx-auto bg-gradient-to-r from-roundone-purple/10 to-roundone-light-purple/10 rounded-xl p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Need a custom solution?</h3>
                  <p className="text-muted-foreground">
                    Contact our sales team to create a plan tailored to your organization's unique requirements.
                  </p>
                </div>
                <Button 
                  className="gradient-button whitespace-nowrap rounded-full"
                  onClick={() => navigate('/contact')}
                >
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>
        </section>
        
        {/* Replace FAQ section with the shared component */}
        <FrequentlyAskedQuestions />
        
        {/* Add ProvenResults component */}
        <ProvenResults />
      </main>
      
      {/* Use the shared Footer component */}
      <Footer />
    </div>
  );
};

export default Pricing;
